/***
 *
 *   PRODUCT EVENTS — METADATA COLUMNS
 *   Flattens each event's metadata into one table column per top-level key,
 *   so fields like `reason` on account_closed are scannable (CORE-4277).
 *   Column keys are prefixed so a metadata key can never collide with the
 *   base columns (time, user_email).
 *
 **********/

const PREFIX  = 'md:';
const RAW_KEY = 'md-raw'; // metadata that isn't a JSON object (string, array, number)

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

// pg returns json columns parsed, but text columns arrive as strings — accept
// both, and keep an unparseable string as-is rather than throwing.
export function parseMetadata(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw !== 'string') return raw;
  try { return JSON.parse(raw); }
  catch { return raw; }
}

export function formatMetadataValue(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// One column per top-level key, in first-seen order across the page, plus a
// single `metadata` column when any row carries non-object metadata.
export function metadataColumns(rows) {
  const keys = [];
  const seen = new Set();
  let hasRaw = false;

  for (const row of rows) {
    const md = parseMetadata(row.metadata);
    if (md === null) continue;
    if (!isPlainObject(md)) { hasRaw = true; continue; }
    for (const key of Object.keys(md)) {
      if (!seen.has(key)) { seen.add(key); keys.push(key); }
    }
  }

  const cols = keys.map(key => ({ key: `${PREFIX}${key}`, label: key }));
  if (hasRaw) cols.push({ key: RAW_KEY, label: 'metadata' });
  return cols;
}

export function flattenMetadata(raw) {
  const md = parseMetadata(raw);
  if (md === null) return {};
  if (!isPlainObject(md)) return { [RAW_KEY]: formatMetadataValue(md) };

  const out = {};
  for (const [key, value] of Object.entries(md)) {
    out[`${PREFIX}${key}`] = formatMetadataValue(value);
  }
  return out;
}

export function isMetadataColumn(key) {
  return key === RAW_KEY || key.startsWith(PREFIX);
}
